import { ChangeDetectionStrategy, Component, computed, input, model, output, ViewEncapsulation } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { Workflow } from "../service/Workflow";
import { WorkflowActionInputComponent } from "./workflow.action.input.component";

@Component({
    standalone: true,
    selector: "s25-ng-workflow-create-event-action",
    template: `
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['eventName']?.inputValue"
            (valueChange)="setInputValue('eventName', $event)"
            [label]="'Event Name'"
            [type]="'text'"
            [required]="true"
            [help]="'Use field variables like $CNET-123-EVENT_NAME'"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['startDate']?.inputValue"
            (valueChange)="setInputValue('startDate', $event)"
            [label]="'Start Date'"
            [type]="'text'"
            [required]="!hasOccurrences()"
            [help]="
                hasOccurrences()
                    ? 'Optional if Location Occurrences provided (will use min occurrence start)'
                    : 'Use field variables like $CNET-123-START_DATE'
            "
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['endDate']?.inputValue"
            (valueChange)="setInputValue('endDate', $event)"
            [label]="'End Date'"
            [type]="'text'"
            [required]="!hasOccurrences()"
            [help]="
                hasOccurrences()
                    ? 'Optional if Location Occurrences provided (will use max occurrence end)'
                    : 'Use field variables like $CNET-123-END_DATE'
            "
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['eventTypeId']?.inputValue"
            (valueChange)="setInputValue('eventTypeId', $event)"
            [label]="'Event Type ID'"
            [type]="'text'"
            [required]="true"
            [help]="'Use field variables like $CNET-123-EVENT_TYPE_ID'"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['primaryOrgId']?.inputValue"
            (valueChange)="setInputValue('primaryOrgId', $event)"
            [label]="'Primary Organization ID'"
            [type]="'text'"
            [required]="false"
            [help]="'Use field variables like $CNET-123-ORG_ID (optional)'"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['objOccurrences']?.inputValue"
            (valueChange)="setInputValue('objOccurrences', $event)"
            [label]="'Location Occurrences'"
            [type]="'text'"
            [required]="false"
            [help]="
                'Use field variable for OBJECT_OCCURRENCE field like $CNET-123-OCCURRENCES (optional - if provided, start/end dates will be calculated from occurrences)'
            "
        />
        <h3>Registration</h3>
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['enableRegistration']?.inputValue"
            (valueChange)="setInputValue('enableRegistration', $event)"
            [label]="'Enable Registration'"
            [type]="'text'"
            [required]="false"
            [help]="'Use field variables like $CNET-123-ENABLE_REGISTRATION'"
        />
        @if (inputMap()["enableRegistration"]?.inputValue) {
            <s25-ng-workflow-action-input
                class="system-input"
                [value]="inputMap()['registrationFormId']?.inputValue"
                (valueChange)="setInputValue('registrationFormId', $event)"
                [label]="'Registration Form ID'"
                [type]="'text'"
                [required]="false"
                [help]="'Use field variables like $CNET-123-REGISTRATION_FORM_ID'"
            />
            <s25-ng-workflow-action-input
                class="system-input"
                [value]="inputMap()['registrationStart']?.inputValue"
                (valueChange)="setInputValue('registrationStart', $event)"
                [label]="'Registration Start Date'"
                [type]="'text'"
                [required]="true"
                [help]="'Use field variables like $CNET-123-REGISTRATION_START_DATE'"
            />
            <s25-ng-workflow-action-input
                class="system-input"
                [value]="inputMap()['registrationEnd']?.inputValue"
                (valueChange)="setInputValue('registrationEnd', $event)"
                [label]="'Registration End Date'"
                [type]="'text'"
                [required]="true"
                [help]="'Use field variables like $CNET-123-REGISTRATION_END_DATE'"
            />
            <s25-ng-workflow-action-input
                class="system-input"
                [value]="inputMap()['registrants']?.inputValue"
                (valueChange)="setInputValue('registrants', $event)"
                [label]="'Who Can Register'"
                [type]="'text'"
                [required]="true"
                [help]="'Use field variables like $CNET-123-REGISTRANTS'"
            />
        }
    `,
    styles: `
        .system-input {
            margin-bottom: 16px;
        }

        .system-input:last-child {
            margin-bottom: 0;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [ReactiveFormsModule, WorkflowActionInputComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateEventActionComponent {
    readonly inputs = model.required<Workflow.WorkflowActionInput[]>();
    readonly inputIdGenerator = input.required<() => number>();

    readonly changed = output<void>();

    readonly inputMap = computed(() => Object.fromEntries(this.inputs().map((input) => [input.inputCode, input])));

    readonly hasOccurrences = computed(() => {
        const occInput = this.inputMap()["objOccurrences"];
        return occInput?.inputValue && occInput.inputValue.trim().length > 0;
    });

    setInputValue(inputCode: string, value: string) {
        let input = this.inputs().find((input) => input.inputCode === inputCode);
        if (!input) {
            input = {
                id: this.inputIdGenerator()(),
                inputCode: inputCode,
                inputValue: value,
            };
            this.inputs.update((inputs) => [...inputs, input]);
        } else {
            input.inputValue = value;
        }
        this.changed.emit();
    }
}
